import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TagManager from 'react-gtm-module';
import {
  AccountData,
  AccountService,
  AccountStatus,
  SessionOrigin,
  StorefrontButtonShape,
  StorefrontData,
} from '../api';
import { AccountNotActive } from '../shared/ui/AccountNotActive';
import RotatingLoader from '../ui/RotatingLoader';
import { LoaderLogoProvider } from '../ui/LoaderLogo';
import { useLanguageChange } from '../orderPage/hooks/useLanguageChange';
import { setUTMData } from '../orderPage/orderCompletion/utmLocalStorage';
import { useSession } from '../session';
import ErrorBoundary from '../ErrorBoundery';

const AccountDataContext = createContext<AccountData | undefined>(undefined);
const StorefrontContext = createContext<StorefrontData | undefined>(undefined);

export const useAccountData = () => {
  return useContext(AccountDataContext);
};

export const useStorefrontData = () => {
  return useContext(StorefrontContext);
};

const getButtonRadius = (shape?: StorefrontButtonShape): number => {
  switch (shape) {
    case StorefrontButtonShape.SHARP:
      return 0;
    case StorefrontButtonShape.ROUNDED:
      return 24;
    case StorefrontButtonShape.MEDIUM:
    default:
      return 8;
  }
};

const fontFamily = ['Assistant', 'Roboto', 'sans-serif'].join(',');

const loadFont = () => {
  const fontUrl = 'https://fonts.googleapis.com/css2?family=Assistant:wght@300;400;500;600;700&display=swap';
  if (!document.querySelector(`link[href="${fontUrl}"]`)) {
    const link = document.createElement('link');
    link.href = fontUrl;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
};

const createStorefrontTheme = (primaryColor: string, buttonRadius: number, direction: 'ltr' | 'rtl') => {
  // @ts-ignore - customTheme is required by global type augmentation but not needed for storefront
  return createTheme({
    direction,
    palette: {
      primary: {
        main: primaryColor,
      },
      background: {
        default: '#ffffff',
        paper: '#ffffff',
      },
      text: {
        primary: '#1a1a1a',
        secondary: '#666666',
      },
    },
    typography: {
      fontFamily,
      h4: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
      subtitle1: {
        fontWeight: 500,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: buttonRadius,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: buttonRadius,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: buttonRadius,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          'html, body, button, input, select, textarea': {
            fontFamily: `${fontFamily} !important`,
          },
        },
      },
    },
  });
};

export const StorefrontThemeWrapper = ({
  children,
  sessionOrigin,
}: {
  children: React.ReactNode;
  sessionOrigin: SessionOrigin;
}) => {
  const { accountId, storefrontId } = useParams();
  const [searchParams] = useSearchParams();
  const qAccountId = searchParams.get('account_id') || undefined;
  const { i18n } = useTranslation();

  const [account, setAccount] = useState<AccountData | undefined>(undefined);
  const { isLoading: isLoadingLanguage } = useLanguageChange(account?.default_system_language);
  useSession(account?.id, sessionOrigin);

  useEffect(() => {
    loadFont();
    void (async () => {
      const accountData = (await AccountService.getAccount(accountId || qAccountId)) as AccountData;
      setAccount(accountData);
      if (accountData?.marketing_integrations?.gtm_id) {
        TagManager.initialize({ gtmId: accountData.marketing_integrations.gtm_id });
      }
    })();
    setUTMData();
  }, [accountId]);

  if (!account || isLoadingLanguage) {
    return (
      <LoaderLogoProvider account={account}>
        <Grid container justifyContent="center" alignItems="center" minHeight="100vh">
          <RotatingLoader />
        </Grid>
      </LoaderLogoProvider>
    );
  }

  if (account.status === AccountStatus.PAUSED) {
    return <AccountNotActive />;
  }

  const storefront = account.storefronts?.find((s) => s.short_code === storefrontId) || account.storefronts?.[0];

  const primaryColor = storefront?.styling?.button_color || '#16854C';
  const buttonRadius = getButtonRadius(storefront?.styling?.button_shape);
  const direction = i18n.dir();

  const theme = createStorefrontTheme(primaryColor, buttonRadius, direction);

  return (
    <ErrorBoundary>
      <AccountDataContext.Provider value={account}>
        <StorefrontContext.Provider value={storefront}>
          <LoaderLogoProvider account={account}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              {children}
            </ThemeProvider>
          </LoaderLogoProvider>
        </StorefrontContext.Provider>
      </AccountDataContext.Provider>
    </ErrorBoundary>
  );
};
